import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { ThemeProvider } from "styled-components";
import { token } from "../../../../../theme";

interface Cell {
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  neighborCount: number;
}

const GameContainer = styled.div`
  padding: 16px;
  background: ${(props) => props.theme.MSBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-width: 400px;
`;

const GameBoard = styled.div<{ width: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.width}, 20px);
  gap: 1px;
  background: ${(props) => props.theme.darkShadowColor};
  padding: 4px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
`;

const Cell = styled.button<{
  isRevealed: boolean;
  isMine: boolean;
  isFlagged: boolean;
  neighborCount: number;
}>`
  width: 20px;
  height: 20px;
  border: none;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    if (props.isFlagged) {
      return `
        background: ${props.theme.MSBackground};
        box-shadow: ${props.theme.standardBoxShadow};
        color: red;
      `;
    }

    if (!props.isRevealed) {
      return `
        background: ${props.theme.MSBackground};
        box-shadow: ${props.theme.standardBoxShadow};
        
        &:active {
          box-shadow: ${props.theme.invertedStandardBoxShadow};
        }
      `;
    }

    if (props.isMine) {
      return `
        background: red;
        color: ${props.theme.black};
      `;
    }

    const colors = [
      "",
      "blue",
      "green",
      "red",
      "purple",
      "maroon",
      "turquoise",
      "black",
      "gray",
    ];

    return `
      background: ${props.theme.MSBackgroundLight};
      box-shadow: ${props.theme.invertedStandardBoxShadow};
      color: ${colors[props.neighborCount] || "black"};
    `;
  }}
`;

const GameInfo = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  font-size: ${(props) => props.theme.fontNormal}px;
`;

const Counter = styled.div`
  background: ${(props) => props.theme.black};
  color: red;
  padding: 4px 8px;
  font-family: monospace;
  font-size: 14px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
`;

interface MinesweeperProps {
  width?: number;
  height?: number;
  mines?: number;
}

const Minesweeper: React.FC<MinesweeperProps> = ({
  width = 16,
  height = 16,
  mines = 40,
}) => {
  const [board, setBoard] = useState<Cell[][]>([]);
  const [gameStatus, setGameStatus] = useState<"playing" | "won" | "lost">(
    "playing"
  );
  const [flagCount, setFlagCount] = useState(mines);
  const [firstClick, setFirstClick] = useState(true);
  const [timer, setTimer] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);

  const initializeBoard = useCallback(() => {
    const newBoard: Cell[][] = [];
    for (let i = 0; i < height; i++) {
      newBoard[i] = [];
      for (let j = 0; j < width; j++) {
        newBoard[i][j] = {
          isMine: false,
          isRevealed: false,
          isFlagged: false,
          neighborCount: 0,
        };
      }
    }
    return newBoard;
  }, [width, height]);

  const placeMines = useCallback(
    (board: Cell[][], firstClickRow: number, firstClickCol: number) => {
      const newBoard = board.map((row) => row.map((cell) => ({ ...cell })));
      let minesPlaced = 0;

      while (minesPlaced < mines) {
        const row = Math.floor(Math.random() * height);
        const col = Math.floor(Math.random() * width);

        // Don't place mine on first click or if already has mine
        if (
          !newBoard[row][col].isMine &&
          !(row === firstClickRow && col === firstClickCol)
        ) {
          newBoard[row][col].isMine = true;
          minesPlaced++;
        }
      }

      // Calculate neighbor counts
      for (let i = 0; i < height; i++) {
        for (let j = 0; j < width; j++) {
          if (!newBoard[i][j].isMine) {
            let count = 0;
            for (let di = -1; di <= 1; di++) {
              for (let dj = -1; dj <= 1; dj++) {
                const ni = i + di;
                const nj = j + dj;
                if (
                  ni >= 0 &&
                  ni < height &&
                  nj >= 0 &&
                  nj < width &&
                  newBoard[ni][nj].isMine
                ) {
                  count++;
                }
              }
            }
            newBoard[i][j].neighborCount = count;
          }
        }
      }

      return newBoard;
    },
    [width, height, mines]
  );

  const revealCell = useCallback(
    (row: number, col: number) => {
      // Start timer on first click
      if (firstClick && !timerStarted) {
        setTimerStarted(true);
      }

      setBoard((currentBoard) => {
        const newBoard = currentBoard.map((r) => r.map((c) => ({ ...c })));

        if (firstClick) {
          const boardWithMines = placeMines(newBoard, row, col);
          setFirstClick(false);
          return revealCellRecursive(boardWithMines, row, col);
        }

        return revealCellRecursive(newBoard, row, col);
      });
    },
    [firstClick, placeMines, timerStarted]
  );

  const revealCellRecursive = (
    board: Cell[][],
    row: number,
    col: number
  ): Cell[][] => {
    if (
      row < 0 ||
      row >= height ||
      col < 0 ||
      col >= width ||
      board[row][col].isRevealed ||
      board[row][col].isFlagged
    ) {
      return board;
    }

    board[row][col].isRevealed = true;

    if (board[row][col].isMine) {
      setGameStatus("lost");
      // Reveal all mines
      for (let i = 0; i < height; i++) {
        for (let j = 0; j < width; j++) {
          if (board[i][j].isMine) {
            board[i][j].isRevealed = true;
          }
        }
      }
      return board;
    }

    // If cell has no neighboring mines, reveal neighbors
    if (board[row][col].neighborCount === 0) {
      for (let di = -1; di <= 1; di++) {
        for (let dj = -1; dj <= 1; dj++) {
          revealCellRecursive(board, row + di, col + dj);
        }
      }
    }

    return board;
  };

  const toggleFlag = useCallback(
    (e: React.MouseEvent, row: number, col: number) => {
      e.preventDefault();

      if (board.length === 0 || board[row][col].isRevealed) return;

      const currentCell = board[row][col];

      if (currentCell.isFlagged) {
        // Remove flag
        setBoard((currentBoard) => {
          const newBoard = currentBoard.map((r) => r.map((c) => ({ ...c })));
          newBoard[row][col].isFlagged = false;
          return newBoard;
        });
        setFlagCount((prev) => prev + 1);
      } else if (flagCount > 0) {
        // Add flag (only if we have flags available)
        setBoard((currentBoard) => {
          const newBoard = currentBoard.map((r) => r.map((c) => ({ ...c })));
          newBoard[row][col].isFlagged = true;
          return newBoard;
        });
        setFlagCount((prev) => prev - 1);
      }
    },
    [board, flagCount]
  );

  const resetGame = () => {
    setBoard(initializeBoard());
    setGameStatus("playing");
    setFlagCount(mines);
    setFirstClick(true);
    setTimer(0);
    setTimerStarted(false);
  };

  useEffect(() => {
    setBoard(initializeBoard());
  }, [initializeBoard]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timerStarted && gameStatus === "playing") {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerStarted, gameStatus]);

  // Check win condition
  useEffect(() => {
    if (board.length === 0 || gameStatus !== "playing") return;

    let revealedCount = 0;
    let correctFlags = 0;

    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        if (board[i][j].isRevealed && !board[i][j].isMine) {
          revealedCount++;
        }
        if (board[i][j].isFlagged && board[i][j].isMine) {
          correctFlags++;
        }
      }
    }

    if (revealedCount === width * height - mines) {
      setGameStatus("won");
    }
  }, [board, gameStatus, width, height, mines]);

  return (
    <ThemeProvider theme={token}>
      <GameContainer>
        <GameInfo>
          <Counter>{timer.toString().padStart(3, "0")}</Counter>
          <Counter>{flagCount.toString().padStart(3, "0")}</Counter>
          <button
            onClick={resetGame}
            style={{
              background: token.MSBackground,
              boxShadow: token.standardBoxShadow,
              border: "none",
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            {gameStatus === "won" ? "😎" : gameStatus === "lost" ? "😵" : "🙂"}
          </button>
          <div>Status: {gameStatus}</div>
        </GameInfo>

        <GameBoard width={width}>
          {board.map((row, i) =>
            row.map((cell, j) => (
              <Cell
                key={`${i}-${j}`}
                isRevealed={cell.isRevealed}
                isMine={cell.isMine}
                isFlagged={cell.isFlagged}
                neighborCount={cell.neighborCount}
                onClick={() => gameStatus === "playing" && revealCell(i, j)}
                onContextMenu={(e) =>
                  gameStatus === "playing" && toggleFlag(e, i, j)
                }
                disabled={gameStatus !== "playing"}
              >
                {cell.isFlagged
                  ? "🚩"
                  : cell.isRevealed
                  ? cell.isMine
                    ? "💣"
                    : cell.neighborCount > 0
                    ? cell.neighborCount
                    : ""
                  : ""}
              </Cell>
            ))
          )}
        </GameBoard>
      </GameContainer>
    </ThemeProvider>
  );
};

export default Minesweeper;
